<template>
  <div class="testimonial-story">
    <div class="hero-section">
      <div class="container">
        <div class="row centered">
          <div class="info col col-4 sm-6">
            <div class="back back-desktop">
              <nuxt-link to="/success-stories/">
                <IconBack /> All stories
              </nuxt-link>
            </div>
            <h1 class="h2">
              {{ testimonial.name }}
            </h1>
            <p>{{ testimonial.description }}</p>
          </div>
          <div class="col col-4 sm-5">
            <div class="back back-mobile">
              <nuxt-link to="/success-stories/">
                <IconBack /> All stories
              </nuxt-link>
            </div>
            <div class="img">
              <img :src="`${$config.imagesUrl}/success-stories/${testimonial.img}`" :alt="testimonial.name">
            </div>
          </div>
        </div>
      </div>
    </div>
    <section class="content-wrap container">
      <h2 class="h3">
        {{ testimonial.title }}
      </h2>
      <div class="content" v-html="testimonial.content" />
    </section>
    <PreFooter
      title="Ready to Start Your Data Science Career?"
      description="Learn from the best, gain the top skills required by employers, and secure a role in data science."
      button-title="Start Your Career"
      button-link="/signup/"
    />
    <section class="site-section dark">
      <div class="container">
        <Slider :disabled="stories.length <= 3" dark class="testimonials-slider">
          <h4 slot="heading" class="h3">
            See More Stories
          </h4>
          <nuxt-link slot="controls" to="/success-stories/">
            Show All
          </nuxt-link>
          <div
            v-for="testim in stories"
            :key="testim.id"
            slot="slide"
            class="col col-4 sm-6"
          >
            <nuxt-link
              :to="`/success-stories/${testim.slug}/`"
              class="story-card"
            >
              <div class="image">
                <img loading="lazy" :src="`${$config.imagesUrl}/success-stories/${testim.img}`" :alt="testim.name">
              </div>
              <div class="info">
                <h5 class="h5">
                  {{ testim.name }}
                </h5>
                <div>
                  {{ testim.description }}
                </div>
                <ButtonArrow
                  class="btn-mobile"
                  type="span"
                  text="Read Story"
                />
              </div>
            </nuxt-link>
          </div>
        </Slider>
      </div>
    </section>
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import Slider from '@itbpbg/lms-components/src/components/Slider';
import ButtonArrow from '@itbpbg/lms-components/src/components/ButtonArrow';
import PreFooter from '../../components/PreFooter';
import IconBack from '../../assets/svg/icon-back.svg?vue-component';

export default {
  components: {
    Slider,
    ButtonArrow,
    PreFooter,
    IconBack,
  },
  mixins: [global],
  async asyncData({
    $axios, route, $config, error,
  }) {
    const [{ testimonial, stories }, page] = await Promise.all([
      $axios.$get(`/testimonials/${route.params.id}`).catch(() => false),
      $axios.$get(`/pages/${route.params.id}`).catch(() => ({})),
    ]);

    if (!testimonial || $config.platform !== 'ds') {
      return error({ title: 'Not found', statusCode: 404, message: 'Testimonial story not found' });
    }

    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.metaFullImageUrl = `${$config.siteUrl}/resources/${testimonial.img}`;

    page.created = testimonial.created || new Date();
    page.updated = testimonial.updated || new Date();

    return {
      testimonial,
      stories,
      page,
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: { ...this.page } },
      {
        key: 'Person',
        values: {
          '@type': 'Person',
          name: this.testimonial.name,
          url: `${this.$config.siteUrl}${this.$route.fullPath}`,
          image: {
            '@type': 'ImageObject',
            url: `${this.$config.imagesUrl}/success-stories/${this.testimonial.img}`,
            caption: `${this.testimonial.name}'s image`,
          },
        },
      },
      {
        key: 'ImageObject',
        values: {
          '@id': `${this.page.fullPath}#primaryimage`,
          url: this.page.metaFullImageUrl,
          width: this.page.metaImageWidth,
          height: this.page.metaImageHeight,
          caption: this.testimonial.title,
        },
      },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps(this.page),
      script: [linkedData],
    };
  },
  mounted() {
    const setBlockquoteImage = () => {
      if (this.$store.state.layout === 'desktop') {
        document.querySelector('.blockquote-big').style.backgroundImage = `url(${this.$config.imagesUrl}/success-stories/${this.testimonial.bigImg})`;
      } else {
        document.querySelector('.blockquote-big').style.backgroundImage = `url(${this.$config.imagesUrl}/success-stories/mobile-${this.testimonial.bigImg})`;
      }
    };

    setBlockquoteImage();

    window.addEventListener('resize', setBlockquoteImage);
  },
};
</script>

<style lang="scss">
.testimonial-story {
  .hero-section {
    min-height: 780px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(253.23deg, rgba(0, 164, 178, 0.2) 0%, rgba(0, 164, 178, 0.05) 44.75%, rgba(0, 164, 178, 0) 94.77%);
      background-color: var(--main-dark);
      transform: matrix(-1, 0, 0, 1, 0, 0);
    }

    img {
      width: 100%;
      max-width: 370px;
      border-radius: 10px;
    }

    .back {
      margin-bottom: var(--spacing-24);

      a {
        font-size: 18px;
        font-weight: 500;
        color: var(--main-dark-shade-4);

        svg {
          margin-right: 10px;
        }
      }
    }

    .back-mobile {
      display: none;
    }

    .h2 {
      color: var(--true-white);
      margin-bottom: var(--spacing-36);
    }

    p {
      font-size: 18px;
      color: var(--main-dark-shade-4);
    }

    .info {
      margin-right: var(--spacing-20);
    }
  }

  .content-wrap {
    .h3 {
      max-width: 770px;
      margin: var(--spacing-80) auto var(--spacing-48);
    }

    .content {
      margin-bottom: 52px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      > * {
        max-width: 770px;
        margin: 0 auto;
      }

      br {
        display: block;
        margin-bottom: 28px;
        content: " ";
      }

      p {
        font-size: 18px;
        color: var(--main-dark-shade-0);
        margin: 0;
      }

      blockquote:not(.blockquote-big) {
        margin: var(--spacing-80) 0 var(--spacing-48);
        position: relative;
        border-left: 1px solid var(--main-dark-shade-4);
        padding-left: 19px;

        &:before {
          content: '';
          position: absolute;
          left: 0;
          top: -33px;
          width: 24px;
          height: 17px;
          background-image: url('../../assets/svg/icon-quotes.svg');
          image-rendering: -webkit-optimize-contrast;
          background-size: cover;
          display: block;
          margin-bottom: 16px;
        }

        p {
          font-size: 21px;
          color: var(--main-dark-light-4);
        }
      }

      .blockquote-big {
        max-width: 100%;
        min-height: 590px;
        margin-top: var(--spacing-48);
        margin-bottom: var(--spacing-48);
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
        border-radius: 14px;

        p {
          position: relative;
          padding: 150px 590px 88px 100px;
          font-size: 28px;
          font-weight: 500;
          line-height: 140%;
          color: var(--true-white);

          &:before {
            content: '';
            position: absolute;
            top: 87px;
            left: 100px;
            width: 35px;
            height: 27px;
            background-image: url('../../assets/images/icon-quote-testimonial-story.png');
            image-rendering: -webkit-optimize-contrast;
            background-size: cover;
          }
        }
      }
    }
  }

  .site-section {
    &.dark {
      background-color: var(--main-dark-light-1);
      padding-top: var(--spacing-100);
      padding-bottom: var(--spacing-120);
    }
  }

  .testimonials-slider {
    .heading {
      .h3 {
        margin-bottom: 0;
      }
    }

    .story-card {
      display: block;
      background-color: var(--main-dark-light-2);
      border-radius: 7px;
    }

    .image {
      img {
        width: 100%;
        border-radius: 7px 7px 0 0;
      }
    }

    .info {
      padding: 25px 24px 30px;

      .h5 {
        color: var(--true-white);
        margin-bottom: var(--spacing-8);
      }

      div {
        color: var(--main-dark-light-4);
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .btn-arrow {
        margin-top: var(--spacing-24);
      }
    }
  }

  .pre-footer {
    padding-top: var(--spacing-120);

    .bg {
      margin-top: var(--spacing-56);
    }
  }

  .slider-header {
    margin-bottom: var(--spacing-30);

    .controls {
      align-self: flex-end;
    }
  }
}

@include bp($bp-laptop) {
  .testimonial-story {
    .h3 {
      text-align: center;
      max-width: 650px;
    }

    .content-wrap {
      .content {
        > * {
          max-width: 650px;
        }

        .blockquote-big {
          max-height: 590px;

          p {
            padding: 150px 495px 88px 100px;
            font-size: 24px;
          }
        }
      }
    }

    .site-section {
      &.dark {
        padding-bottom: var(--spacing-100);
      }
    }
  }
}

@include bp($bp-tablet) {
  .hero-section img {
    max-width: 100%;
  }
}

@include bp($bp-mobile) {
  .testimonial-story {
    .hero-section {
      padding: 168px 0 52px;

      .col-4:first-of-type {
        order: 1;
        margin-top: var(--spacing-36);
      }

      .back-desktop {
        display: none;
      }

      .back-mobile {
        display: block;
        margin-bottom: var(--spacing-36);
      }

      .info {
        margin-right: 0;
      }
    }

    .content-wrap .content {
      .blockquote-big {
        background-color: var(--main-dark);

        &:before {
          content: '';
          height: 450px;
          width: 100%;
          display: block;
        }

        p {
          font-size: 21px;
          padding: 0 var(--spacing-24);

          &:before {
            left: 24px;
            top: -63px;
          }
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .testimonial-story {
    .hero-section {
      img {
        width: 100%;
        max-width: 100%;
        border-radius: 10px;
      }
    }

    .content-wrap .content {
      margin-left: -24px;
      margin-right: -24px;

      p,
      blockquote:not(.blockquote-big) {
        margin-left: var(--spacing-24);
        margin-right: var(--spacing-24);
      }

      .blockquote-big {
        background-size: auto 350px;
        background-position-y: top;
        border-radius: 0;

        &:before {
          content: '';
          height: 360px;
        }

        p {
          padding-bottom: var(--spacing-36);
        }
      }
    }

    .pre-footer {
      padding-top: var(--spacing-120);

      .subtitle {
        width: auto;
        margin-bottom: var(--spacing-44);
      }
    }
  }
}
</style>
